import { getCustomerInformation } from "../utils/API/Customer/customerAPI";
import { useCustomerInformationStore } from "../components/checkout/stores/customerInformationStore";
import {
    CheckoutStage,
    CheckoutStages,
    CheckoutStep,
    CheckoutSteps,
} from "./models/checkout.models";
import { useCheckoutStateStore } from "../components/checkout/stores/checkoutStateStore";
import {
    AddressModel,
    AddressModelSchema,
    AddressModelWithoutAnalytics,
    AddressModelWithoutAnalyticsSchema,
    Cart,
    CartSchema,
} from "../utils/API/Cart/cartAPISchema";
import { getCityFromPostalCode } from "../utils/API/Postal/postalAPI";
import { USER_FEEDBACK } from "../utils/API/APIErrors";
import { FEEDBACK_TEXT } from "../utils/validationHelpers";
import { useDeliveryInformationStore } from "../components/checkout/stores/deliveryInformationStore";
import {
    createAddDeliveryMethodRequestDtoSchema,
    SetDeliveryOptionResponseModel,
    UpdateDeliveryRequestDto,
} from "../utils/API/Delivery/deliveryAPISchema";
import {
    getDeliveryInformation,
    setDeliveryOption,
} from "../utils/API/Delivery/deliveryAPI";
import { getCart, updateCustomer } from "../utils/API/Cart/cartAPI";
import CheckoutHelpers from "../../../helpers/checkoutHelpers";
import { useShoppingCartStore } from "../components/shoppingCart/stores/shoppingCartStore";
import { nextTick } from "vue";
import GtmService from "./gtm.service";
import { VoyadoEngageWebTrackingService } from "./voyadoEngageWebTracking.service";
import { mapCartToVoyadoEngageCart } from "./mappers/voyadoEngageWebTracking.mapper";
import { Log } from "../utils/API/Logging/LoggingAPI";

export default class CheckoutService {
    /**
     * Attempts to retrieve customer information from the API.
     * If no customer information is found, verification is needed (User is not logged in).
     *
     * @returns {boolean} Returns true if verification is needed, false otherwise.
     */
    private static verificationIsNeeded = async () => {
        const customerInformationStore = useCustomerInformationStore();

        const customerData = await this.apiGetCustomerInformation();
        if (!customerData) return true;

        customerInformationStore.setFields(customerData);
        return false;
    };

    /**
     * Fetches customer information from the API and updates the customerInformation value.
     * Sets the cartIsLoading value to true before making the API call and sets it back to false after the call is completed.
     *
     * @returns {Promise<void>} A promise that resolves when the customer information is fetched and updated successfully.
     */
    private static apiGetCustomerInformation = async () => {
        try {
            const data = await getCustomerInformation();

            // Convert null fields to empty strings
            for (let key in data) {
                if (data[key] === null) {
                    data[key] = "";
                }
            }

            return data;
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * Checks if a step in the checkout process has been completed.
     *
     * @param step - The step to check.
     * @returns A promise that resolves to a boolean indicating whether the step has been completed.
     */
    private static stepCompleted = async (
        step: CheckoutStep,
    ): Promise<boolean> => {
        const checkoutStateStore = useCheckoutStateStore();

        if (checkoutStateStore.isLoading) return false;
        checkoutStateStore.setIsLoading(true);

        if (step === CheckoutSteps.CustomerInformation) {
            const result = await this.submitCustomerInformationForm();
            checkoutStateStore.setIsLoading(false);
            return result;
        }

        if (step === CheckoutSteps.Delivery) {
            const result = await this.submitDeliveryInformationForm();
            checkoutStateStore.setIsLoading(false);
            return result;
        }

        checkoutStateStore.setIsLoading(false);
        return false;
    };

    /**
     * Triggers the submission of the customer information form.
     * If the form is valid, the customer information is updated using the API.
     * If the form is invalid, the form errors are set and the form is not submitted.
     *
     * @returns {Promise<void>} A promise that resolves when the form is submitted successfully.
     */
    private static submitCustomerInformationForm = async () => {
        const customerInformationStore = useCustomerInformationStore();

        customerInformationStore.setErrorSummaryVisible(true);

        const validationResult = AddressModelWithoutAnalyticsSchema.safeParse(
            customerInformationStore.fields,
        );

        if (!validationResult.success) {
            customerInformationStore.clearAllErrors();

            validationResult.error.issues.forEach((issue) => {
                const firstPathSegment = issue.path?.[0];
                const key = String(firstPathSegment ?? ""); // ensure string key
                if (!key) return;

                customerInformationStore.setError(key, [
                    ...(customerInformationStore.errors[key] ?? []),
                    issue.message,
                ]);
            });
        }

        let matchingCityResult;
        if (validationResult.success) {
            matchingCityResult = await this.validatePostalCodeAndCity(
                validationResult.data.postalCode,
                validationResult.data.city,
            );
            if (!matchingCityResult) return false;

            if (!validationResult.data.bringAddressId) {
                this.tryToGetAddressIdFromSuggestions(validationResult);
            }
        }

        if (
            !validationResult.success ||
            matchingCityResult == USER_FEEDBACK.UNKNOWN_POSTALCODE
        ) {
            return false;
        }

        const success = await this.apiUpdateCustomer(validationResult.data);
        if (!success) return false;

        this.setSubmittedCustomerInformation(validationResult.data);
        customerInformationStore.clearAllErrors();
        return true;
    };

    /**
     * Submits the delivery information form.
     * If the form is valid, the delivery information is updated using the API.
     * If the form is invalid, the form errors are set and the form is not submitted.
     *
     * @returns {boolean} Returns `true` if the form submission is successful, otherwise `false`.
     */
    static submitDeliveryInformationForm = async () => {
        const deliveryInformationStore = useDeliveryInformationStore();

        if (
            !deliveryInformationStore.selectedDeliveryMethod ||
            !deliveryInformationStore.selectedDeliveryMethod?.id
        ) {
            deliveryInformationStore.setError(
                "deliveryMethod",
                FEEDBACK_TEXT().REQUIRED_DELIVERY_METHOD,
            );
            return false;
        }

        const requestBody: UpdateDeliveryRequestDto = {
            deliveryLocationId:
                deliveryInformationStore.selectedDeliveryLocation?.id ??
                undefined,
        };

        if (deliveryInformationStore.selectedDeliveryWindow) {
            requestBody.deliveryWindow =
                deliveryInformationStore.selectedDeliveryWindow ?? undefined;
        }

        const validationSchema = createAddDeliveryMethodRequestDtoSchema(
            deliveryInformationStore.selectedDeliveryMethod,
        );

        const validationResult = validationSchema.safeParse(requestBody);

        if (!validationResult.success) {
            this.setDeliveryInformationErrors(validationResult.error);
            return false;
        }

        const data = await this.apiUpdateDelivery(
            deliveryInformationStore.selectedDeliveryMethod.id,
            requestBody,
        );

        if (!data) return false;

        const shoppingCartStore = useShoppingCartStore();
        deliveryInformationStore.clearAllErrors();
        shoppingCartStore.setCart(data.cartDto);
        return true;
    };

    /**
     * Updates the customer information using the API.
     *
     * @async
     * @function apiUpdateCustomer
     * @returns {Promise} A promise that resolves when the customer information is successfully updated.
     */
    private static apiUpdateCustomer = async (
        customerInformation: AddressModelWithoutAnalytics,
    ): Promise<boolean> => {
        try {
            const success = await updateCustomer(customerInformation);
            if (success) return true;
            return false;
        } catch (error) {
            if (error instanceof Error) {
                console.log(error.message);
            }
        }
        return false;
    };

    /**
     * Updates the delivery method for the checkout.
     *
     * @param {string} deliveryMethodId - The ID of the delivery method.
     * @param {UpdateDeliveuseCustomerInformationStore.ClearAllErrorso} request - The request object containing the updated delivery information.
     * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating whether the update was successful.
     */
    private static apiUpdateDelivery = async (
        deliveryMethodId: string,
        request: UpdateDeliveryRequestDto,
    ): Promise<SetDeliveryOptionResponseModel | null> => {
        try {
            const data = await setDeliveryOption(deliveryMethodId, request);
            return data;
        } catch (error) {
            if (error instanceof Error) {
                console.log(error.message);
            }
        }
        return null;
    };

    /**
     * Sets the delivery information errors.
     *
     * @param {Object} errors - The errors object containing the path and message.
     */
    private static setDeliveryInformationErrors = (errors) => {
        const deliveryInformationStore = useDeliveryInformationStore();
        deliveryInformationStore.setErrors(errors);
    };

    /**
     * Checks if all checkout steps prior to the given toStep have been completed.
     *
     * @param {CheckoutStep} toStep - The step to check against.
     * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating whether all prior steps have been completed.
     */
    private static priorStepsCompleted = async (toStep: CheckoutStep) => {
        for (const step of Object.values(CheckoutSteps)) {
            if (step === toStep) {
                return true;
            }

            const completed = await this.stepCompleted(step);
            if (!completed) {
                return false;
            }
        }

        return true;
    };

    /**
     * Navigates to the specified checkout step if the current step is completed.
     * This is necessary to ensure that required data for the next step is available before use.
     *
     * @param {CheckoutStep} toStep - The checkout step to navigate to.
     */
    static goToCheckoutStep = async (toStep: CheckoutStep) => {
        const okToProceed = await this.priorStepsCompleted(toStep);
        const checkoutStateStore = useCheckoutStateStore();

        if (checkoutStateStore.currentCheckoutStep === toStep || !okToProceed)
            return;

        if (
            toStep == CheckoutSteps.Delivery &&
            checkoutStateStore.currentCheckoutStep ===
                CheckoutSteps.CustomerInformation
        ) {
            await this.apiGetDeliveryInformation();
        }

        GtmService.pushCheckoutEvent(
            CheckoutHelpers.toGoogleAnalyticsEventName(toStep),
        );
        nextTick(() => (checkoutStateStore.currentCheckoutStep = toStep));
    };

    /**
     * Fetches the delivery information from the API and updates the deliveryInformation and selectedDeliveryMethodId variables.
     *
     * @returns {Promise<void>} A promise that resolves when the delivery information is fetched and updated successfully.
     */
    private static apiGetDeliveryInformation = async () => {
        const deliveryInformationStore = useDeliveryInformationStore();
        const checkoutStateStore = useCheckoutStateStore();

        try {
            const data = await getDeliveryInformation();
            deliveryInformationStore.deliveryInformation = data.deliveryDto;

            deliveryInformationStore.setSelectedDeliveryMethod(
                data.deliveryDto.deliveryMethods?.find(
                    (option) => option.isDefaultSelection,
                ),
            );
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * Fetches the shopping cart data from the API and updates the shopping cart store.
     *
     * This function asynchronously calls the `getShoppingCart` API to retrieve the current
     * shopping cart data. Upon successful retrieval, it updates the shopping cart store
     * with the fetched data. If an error occurs during the API call, it logs the error to the console.
     *
     * @async
     * @function apiGetShoppingCart
     * @returns {Promise<void>} A promise that resolves when the shopping cart data has been fetched and the store updated.
     */
    static apiGetShoppingCart = async () => {
        try {
            const data = await getCart();
            const newCart = CartSchema.parse(data.cartDto);
            VoyadoEngageWebTrackingService.trackCart(
                mapCartToVoyadoEngageCart(newCart),
            );
            return newCart;
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * Sets the submitted customer information.
     *
     * @param {AddressModel} customerInformation - The customer information to be set.
     */
    private static setSubmittedCustomerInformation = (
        customerInformation: AddressModelWithoutAnalytics,
    ) => {
        const customerInformationStore = useCustomerInformationStore();
        customerInformationStore.setSubmittedFields(customerInformation);
    };

    static goToCheckoutStage = async (stage: CheckoutStage) => {
        const checkoutStateStore = useCheckoutStateStore();

        if (stage === CheckoutStages.Verification) {
            checkoutStateStore.setIsLoading(true);

            const verificationNeeded = await this.verificationIsNeeded();
            stage = verificationNeeded
                ? CheckoutStages.Verification
                : CheckoutStages.Checkout;

            checkoutStateStore.setIsLoading(false);
        }

        if (stage === CheckoutStages.Checkout) {
            GtmService.pushCheckoutEvent(
                CheckoutHelpers.toGoogleAnalyticsEventName(
                    CheckoutSteps.CustomerInformation,
                ),
            );
        }

        checkoutStateStore.setCheckoutStage(stage);
    };

    static goToCheckoutStageAfterLogin = async () => {
        const checkoutStateStore = useCheckoutStateStore();
        const customerInformationStore = useCustomerInformationStore();

        const customerInformation = await this.apiGetCustomerInformation();
        customerInformationStore.setFields(customerInformation);

        checkoutStateStore.currentCheckoutStage = CheckoutStages.Checkout;
        GtmService.pushCheckoutEvent(
            CheckoutHelpers.toGoogleAnalyticsEventName(
                CheckoutSteps.CustomerInformation,
            ),
        );
        if (customerInformation?.loyaltyClubId) {
            VoyadoEngageWebTrackingService.setContactId(
                customerInformation.loyaltyClubId,
            );
        }
    };

    /**
     * Retrieves the city matching the given postal code.
     *
     * @param {string} postalCode - The postal code to match.
     * @returns {Promise<string>} - A promise that resolves to the matching city.
     */
    static TryGetCityMatchingPostalCode = async (postalCode: string) => {
        if (!postalCode) return;
        const customerInformationStore = useCustomerInformationStore();
        customerInformationStore.setRetrievingCity(true);

        const matchingCityResult = await getCityFromPostalCode(postalCode);

        customerInformationStore.setRetrievingCity(false);
        return matchingCityResult;
    };

    /**
     * Validates the postal code and city.
     *
     * @param {string} postalCode - The postal code to validate.
     * @param {string} city - The city to validate.
     * @returns {Promise<boolean>} - A promise that resolves to true if the postal code and city match, false otherwise.
     */
    static validatePostalCodeAndCity = async (
        postalCode: string,
        city: string,
    ) => {
        const customerInformationStore = useCustomerInformationStore();
        const matchingCityResult = await getCityFromPostalCode(postalCode);

        if (matchingCityResult === USER_FEEDBACK.UNKNOWN_POSTALCODE) {
            customerInformationStore.setError("postalCode", [
                FEEDBACK_TEXT().UNKNOWN_POSTALCODE,
            ]);
            return false;
        }

        if (matchingCityResult !== city.toUpperCase()) {
            customerInformationStore.setError("postalCode", [
                FEEDBACK_TEXT().POSTALCODE_AND_CITY_DONT_MATCH,
            ]);
            return false;
        }

        return true;
    };

    /**
     * Attempts to find a matching address suggestion based on the provided validation result.
     * If a match is found, updates the customer information store and the validation result
     * with the corresponding address ID (`bringAddressId`).
     *
     * @param validationResult - The result object containing address, postal code, and city data to validate against suggestions.
     */
    static tryToGetAddressIdFromSuggestions = (validationResult) => {
        const customerInformationStore = useCustomerInformationStore();

        const matchFromSuggestions =
            customerInformationStore.mostRecentAddressSuggestions.find(
                (suggestion) =>
                    CheckoutHelpers.formatAddress(suggestion).toUpperCase() ===
                        validationResult.data.address.toUpperCase() &&
                    suggestion.postalCode ===
                        validationResult.data.postalCode &&
                    suggestion.city.toUpperCase() ===
                        validationResult.data.city.toUpperCase(),
            );

        if (matchFromSuggestions) {
            customerInformationStore.setField(
                "bringAddressId",
                matchFromSuggestions.addressId,
            );
            validationResult.data.bringAddressId =
                matchFromSuggestions.addressId;
        }
    };

    static logCartAvailabilityDifferences = async (checkoutStep: CheckoutStep, firstCart?: Cart, secondCart?: Cart | null, ) => {
        const checkoutStateStore = useCheckoutStateStore();

        if (!secondCart) {
            Log({
                LogLevel: "Warning",
                Message: `Shopping cart is null in checkout step "${checkoutStep}" for paymentId "${checkoutStateStore.paymentId}"`,
            });
            return;
        }

        const differences = CheckoutHelpers.getDifferences(
            firstCart,
            secondCart, 
        );

        if (Object.keys(differences).length > 0) {
            Log({
                LogLevel: "Warning",
                Message: `Shopping cart differences in checkout step "${checkoutStep}"  detected for paymentId "${checkoutStateStore.paymentId}": ${JSON.stringify(differences)}`,
            });
        }
    };
}
